import { EditProvider, SiteFooter, SiteHeader, SiteLinkProvider, useEditContext } from "@quintara/site-kit";
import type { SiteLinkProps } from "@quintara/site-kit";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../common/constants";
import { PAGE_KIT } from "../siteKitMap";
import { PreviewFrame } from "./PreviewFrame";

const CMS_SLUGS = new Set(Object.keys(PAGE_KIT));

// Map an in-site link target to the CMS slug it edits (or null if it has no
// editable page: external links, /contact, /careers, mailto, etc.).
function pathToSlug(to: string): string | null {
  if (!to || /^(https?:|mailto:|tel:)/i.test(to)) return null;
  const clean = to.split("?")[0].split("#")[0].replace(/\/+$/, "");
  if (clean === "" || clean === "/") return "home";
  const seg = clean.replace(/^\//, "");
  return CMS_SLUGS.has(seg) ? seg : null;
}

// The preview is a mini site browser: in browse mode a link to a CMS page opens
// that page's editor; in edit mode links are inert so their text stays editable.
function PreviewLink({ to, className, style, children }: SiteLinkProps) {
  const navigate = useNavigate();
  const { editing } = useEditContext();
  const slug = pathToSlug(to);
  return (
    <a
      className={className}
      style={style}
      href={to || "#"}
      data-cms-nav={!editing && slug ? "true" : undefined}
      onClick={(e) => {
        e.preventDefault();
        if (!editing && slug) navigate(ROUTES.PAGE_EDITOR(slug));
      }}
    >
      {children}
    </a>
  );
}

const NOOP_EDIT = () => {};

export function PagePreview({
  slug,
  content,
  editing = false,
  onEdit,
}: {
  slug: string;
  content: Record<string, unknown>;
  editing?: boolean;
  onEdit?: (path: string, value: string) => void;
}) {
  const entry = PAGE_KIT[slug];
  if (!entry) return null;
  const Preview = entry.Preview;
  return (
    <PreviewFrame>
      <EditProvider value={{ editing, onEdit: onEdit ?? NOOP_EDIT }}>
        <SiteLinkProvider value={PreviewLink}>
          {/* The full site: real header + page + footer, so every nav/footer
              link opens the matching page's editor. content is opaque JSON here;
              the renderer owns its shape. */}
          <SiteHeader />
          <main>
            <Preview content={content as never} />
          </main>
          <SiteFooter />
        </SiteLinkProvider>
      </EditProvider>
    </PreviewFrame>
  );
}
